import { Alert, Button, Card, DatePicker, Form, Input, Select, message } from 'antd';
import dayjs from 'dayjs';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';
import {
  EducationSectionSelect,
  EducationStudentSelect,
} from '@/pages/education/components/EducationLookupSelects';

type FormValues = {
  student_id: string;
  section_id?: string;
  incident_on: dayjs.Dayjs;
  polarity: 'concern' | 'positive';
  category: string;
  severity: string;
  summary: string;
  detail_text?: string;
};

const BEHAVIOUR_DEFAULT_CATEGORY: Record<FormValues['polarity'], string> = {
  concern: 'disruption',
  positive: 'recognition',
};

export default function BehaviourFormPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.behaviour.manage') || user?.permissions?.includes('education.admin')
  );

  const [form] = Form.useForm<FormValues>();
  const polarity = Form.useWatch('polarity', form) ?? 'concern';

  const create = useMutation({
    mutationFn: (values: FormValues) =>
      api.post<{ id: string }>('/education/behaviour/incidents', {
        student_id: values.student_id,
        section_id: values.section_id || undefined,
        incident_on: values.incident_on.format('YYYY-MM-DD'),
        polarity: values.polarity,
        category: values.category,
        severity: values.severity,
        summary: values.summary,
        detail_text: values.detail_text,
      }),
    onSuccess: (res) => {
      message.success(t('behaviourIncidentCreated'));
      const id = res.data?.id;
      navigate(id ? `/education/behaviour/${id}` : '/education/behaviour');
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  if (!canManage) {
    return (
      <EducationPageChrome title={t('newBehaviourIncident')}>
        <Alert type="warning" showIcon message={t('unavailable', { status: 403 })} />
      </EducationPageChrome>
    );
  }

  return (
    <EducationPageChrome
      title={t('newBehaviourIncident')}
      description={t('behaviourIntro')}
      extra={<Button onClick={() => navigate('/education/behaviour')}>{t('behaviour')}</Button>}
    >
      <Card size="small" style={{ maxWidth: 640 }}>
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('behaviourNonSafeguardingHint')}
        />
        <Form
          form={form}
          layout="vertical"
          initialValues={{ incident_on: dayjs(), polarity: 'concern', category: 'disruption', severity: 'low' }}
          onFinish={(values) => create.mutate(values)}
        >
          <Form.Item name="student_id" label={t('student')} rules={[{ required: true }]}>
            <EducationStudentSelect />
          </Form.Item>
          <Form.Item name="section_id" label={t('section')}>
            <EducationSectionSelect allowClear />
          </Form.Item>
          <Form.Item name="incident_on" label={t('incidentOn')} rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} format="DD-MMM-YYYY" />
          </Form.Item>
          <Form.Item name="polarity" label={t('behaviourPolarity', { defaultValue: 'Type' })} rules={[{ required: true }]}>
            <Select
              onChange={(value: FormValues['polarity']) => form.setFieldValue('category', BEHAVIOUR_DEFAULT_CATEGORY[value])}
              options={[
                { value: 'concern', label: t('behaviourPolarityConcern', { defaultValue: 'Concern' }) },
                { value: 'positive', label: t('behaviourPolarityPositive', { defaultValue: 'Positive recognition' }) },
              ]}
            />
          </Form.Item>
          <Form.Item name="category" label={t('category')} rules={[{ required: true }]}>
            <Select
              options={
                polarity === 'positive'
                  ? [
                      { value: 'recognition', label: t('behaviourCategoryRecognition', { defaultValue: 'Recognition' }) },
                      { value: 'leadership', label: t('behaviourCategoryLeadership', { defaultValue: 'Leadership' }) },
                      { value: 'service', label: t('behaviourCategoryService', { defaultValue: 'Service' }) },
                      { value: 'improvement', label: t('behaviourCategoryImprovement', { defaultValue: 'Improvement' }) },
                      { value: 'other_positive', label: t('behaviourCategoryOtherPositive', { defaultValue: 'Other positive' }) },
                    ]
                  : [
                      { value: 'disruption', label: t('behaviourCategoryDisruption') },
                      { value: 'disrespect', label: t('behaviourCategoryDisrespect') },
                      { value: 'property_damage', label: t('behaviourCategoryProperty') },
                      { value: 'peer_conflict', label: t('behaviourCategoryPeer') },
                      { value: 'academic_integrity', label: t('behaviourCategoryAcademic') },
                      { value: 'other', label: t('behaviourCategoryOther') },
                    ]
              }
            />
          </Form.Item>
          <Form.Item name="severity" label={t('severity')} rules={[{ required: true }]}>
            <Select
              options={[
                { value: 'low', label: t('severityLow') },
                { value: 'medium', label: t('severityMedium') },
                { value: 'high', label: t('severityHigh') },
              ]}
            />
          </Form.Item>
          <Form.Item name="summary" label={t('summary')} rules={[{ required: true }]}>
            <Input maxLength={500} />
          </Form.Item>
          <Form.Item name="detail_text" label={t('detail')}>
            <Input.TextArea rows={4} maxLength={2000} />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={create.isPending}>
            {t('save')}
          </Button>
        </Form>
      </Card>
    </EducationPageChrome>
  );
}
